import { ArrowRight, Check, Sparkles, ShieldCheck } from "lucide-react";
import FadeUp from "@/components/landing/FadeUp";
import { PRICING, GUARANTEE } from "./content";
import { slug } from "./utils";
import { Card, PrimaryButton, SecondaryButton, SectionHeading } from "./Primitives";

const GuaranteeBanner = () => (
    <div
        data-testid="pricing-guarantee-banner"
        className="relative overflow-hidden rounded-2xl bg-hire-ink text-white p-7 md:p-9 grid md:grid-cols-12 gap-6 items-center"
    >
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-blue/30 blur-3xl pointer-events-none" />
        <div className="md:col-span-8 flex gap-5 items-start">
            <div className="h-12 w-12 shrink-0 rounded-xl bg-brand-blue grid place-items-center">
                <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
                <p className="text-xs font-semibold tracking-[0.14em] uppercase text-brand-blue-soft">{GUARANTEE.label}</p>
                <h3 className="mt-2 text-xl md:text-2xl font-bold leading-snug">{GUARANTEE.heading}</h3>
                <p className="mt-3 text-sm md:text-[15px] text-white/70 leading-relaxed max-w-2xl">{GUARANTEE.body}</p>
            </div>
        </div>
        <ul className="md:col-span-4 space-y-2.5 md:border-l md:border-white/10 md:pl-6">
            {GUARANTEE.points.map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-sm font-medium">
                    <Check className="h-4 w-4 text-brand-blue-soft shrink-0" /> {p}
                </li>
            ))}
        </ul>
    </div>
);

const Flagship = ({ onSelect }) => {
    const f = PRICING.flagship;
    return (
        <Card
            data-testid="pricing-flagship"
            className="relative overflow-hidden border-brand-blue/40 ring-1 ring-brand-blue/15 shadow-[0_40px_100px_-60px_rgba(45,91,255,0.5)]"
        >
            <div className="grid lg:grid-cols-12">
                <div className="lg:col-span-4 p-7 md:p-10 bg-brand-blue-soft/60 border-b lg:border-b-0 lg:border-r border-brand-blue/10">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-blue text-white px-2.5 py-1 text-[11px] font-semibold">
                            <Sparkles className="h-3 w-3" /> {f.badge}
                        </span>
                        <span className="text-[13px] font-semibold text-hire-ink">{f.name}</span>
                    </div>
                    <p className="mt-6 flex items-baseline gap-2">
                        <span data-testid="flagship-price" className="text-5xl font-extrabold tracking-tight text-hire-ink">{f.price}</span>
                        <span className="text-sm text-hire-muted">{f.priceNote}</span>
                    </p>
                    <h3 className="mt-6 text-xl md:text-[22px] font-bold text-hire-ink leading-snug text-balance">
                        {f.headline}
                    </h3>
                    <p className="mt-4 text-[14.5px] text-hire-muted leading-relaxed">{f.description}</p>
                    <div className="mt-7 rounded-xl bg-hire-ink text-white p-4">
                        <p className="text-[14px] font-semibold leading-snug">{f.highlight}</p>
                    </div>
                    <PrimaryButton
                        data-testid="pricing-cta-hire-assist"
                        onClick={() => onSelect(f.name)}
                        className="mt-7 w-full"
                    >
                        {f.cta} <ArrowRight className="h-4 w-4" />
                    </PrimaryButton>
                </div>
                <div className="lg:col-span-8 p-7 md:p-10 grid md:grid-cols-3 gap-8">
                    {f.sections.map((s, i) => (
                        <div key={s.title}>
                            <p className="text-[11px] font-bold tracking-[0.12em] text-brand-blue">0{i + 1}</p>
                            <h4 className="mt-2 text-[15px] font-semibold text-hire-ink leading-snug">{s.title}</h4>
                            <ul className="mt-4 space-y-2.5">
                                {s.items.map((it) => (
                                    <li key={it} className="flex items-start gap-2 text-[13.5px] text-hire-muted leading-relaxed">
                                        <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-blue" strokeWidth={2.5} />
                                        {it}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
};

const Tier = ({ tier, onSelect }) => (
    <Card
        data-testid={`pricing-tier-${slug(tier.name)}`}
        className="flex flex-col p-7 hover:border-hire-ink/30 hover:shadow-[0_24px_60px_-40px_rgba(11,15,25,0.3)]"
    >
        <p className="text-sm font-semibold text-hire-ink">{tier.name}</p>
        <p className="mt-3 text-4xl font-extrabold tracking-tight text-hire-ink">{tier.price}</p>
        <div className="mt-3 flex flex-wrap gap-2">
            <p data-testid={`pricing-hires-${slug(tier.name)}`} className="inline-flex rounded-md bg-hire-ink text-white px-2 py-1 text-[11.5px] font-semibold">
                {tier.hires}
            </p>
            <p className="inline-flex rounded-md bg-brand-blue-soft text-brand-blue px-2 py-1 text-[11.5px] font-semibold">
                {tier.guarantee}
            </p>
        </div>
        {tier.intro && <p className="mt-6 text-[13.5px] font-medium text-hire-ink">{tier.intro}</p>}
        <ul className={`${tier.intro ? "mt-3" : "mt-6"} space-y-2.5 flex-1`}>
            {tier.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-[13.5px] text-hire-muted leading-relaxed">
                    <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-blue" strokeWidth={2.5} />
                    {it}
                </li>
            ))}
        </ul>
        <SecondaryButton
            data-testid={`pricing-cta-${slug(tier.name)}`}
            onClick={() => onSelect(tier.name)}
            className="mt-8 w-full"
        >
            {tier.cta} <ArrowRight className="h-4 w-4" />
        </SecondaryButton>
    </Card>
);

export const HirePricing = ({ onSelect }) => (
    <section id="pricing" data-testid="pricing-section" className="section-pad bg-hire-bg border-y border-hire-line">
        <div className="container-page">
            <SectionHeading
                eyebrow="Pricing"
                title={PRICING.heading}
                sub={PRICING.sub}
                align="center"
                testId="pricing-heading"
            />

            <FadeUp className="mt-12 md:mt-14">
                <GuaranteeBanner />
            </FadeUp>

            <div className="mt-14 md:mt-16">
                <FadeUp>
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold tracking-[0.14em] uppercase text-brand-blue">Hire only</p>
                        <h3 className="mt-3 text-2xl md:text-3xl font-bold text-hire-ink">{PRICING.hireOnly.name}</h3>
                        <p className="mt-4 text-base text-hire-muted leading-relaxed">{PRICING.hireOnly.description}</p>
                    </div>
                </FadeUp>
                <div className="mt-10 grid md:grid-cols-3 gap-5">
                    {PRICING.hireOnly.tiers.map((t, i) => (
                        <FadeUp key={t.name} delay={i * 0.06}>
                            <Tier tier={t} onSelect={onSelect} />
                        </FadeUp>
                    ))}
                </div>
                <FadeUp delay={0.1}>
                    <p className="mt-8 text-sm font-medium text-hire-ink">{PRICING.hireOnly.note}</p>
                    <p data-testid="pricing-terms" className="mt-2 text-[13px] text-hire-muted">{PRICING.terms}</p>
                </FadeUp>
            </div>

            <div className="mt-16 md:mt-20">
                <FadeUp>
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold tracking-[0.14em] uppercase text-brand-blue">Hire + Assist</p>
                        <h3 className="mt-3 text-2xl md:text-3xl font-bold text-hire-ink">Need more than a hire? We build the system too.</h3>
                    </div>
                </FadeUp>
                <FadeUp delay={0.1} className="mt-10">
                    <Flagship onSelect={onSelect} />
                </FadeUp>
            </div>
        </div>
    </section>
);

export default HirePricing;
